import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSatelliteDish, faCalendarDay } from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../../hooks/useAuth'
import { useAccountContext } from '../../contexts/AccountContext'
import { useMissingReadingsSummary } from '../../hooks/admin/useMissingReadings'
import { useUrlAccount, useUrlBoolean, useUrlNumber } from '../../hooks/useUrlState'
// Shared with the day pages this card links into, so the picker here can never
// offer a day those pages would have to refuse.
import { earliestIso, todayIso } from './missingReadings/DayStepper'
import {
  LabeledSwitch,
  MISSING_READINGS_FLEET_MAX_DAYS,
  RangeToggle,
  ReconfiguredMark,
} from './missingReadings/controls'
import { LoadingSpinner, PanelCard } from '../../shared/components/ui'
import DeliveryBars, { DeliveryLegend } from '../charts/DeliveryBars'
import { deliveryBarsForWindow } from '../../utils/dailyDelivery'
import type { DeviceInfo } from '../../common/enums'
import type { FleetDeviceSummary } from '../../types/missingReadings'

/** Devices that get a daily strip; the API sends a series for this many. */
const STRIP_DEVICES = 10

/**
 * 30 days by default: recent enough that the totals and the worst devices
 * describe the fleet as it is now, not a problem fixed two months ago, and
 * still long enough for a run of bad days to stand out in each strip. 90 and
 * 180 are a click away for a longer view.
 */
const DEFAULT_DAYS = 30

/**
 * Admin-only: fleet-wide missed Sigfox messages, day by day.
 *
 * The headline number on every row is EXACT — gaps in each device's Sigfox
 * sequence number, counted across every message type it sends, so a gap
 * means a transmission never arrived. The expected/missing estimate derived
 * from the device's configured interval only drives the red flag, and is
 * withheld entirely for a day on which a device was reconfigured (marked *),
 * because a configure-then-operate transition makes that estimate meaningless.
 *
 * Both lists are entry points rather than destinations. A day row opens the
 * fleet day page for that day; a worst-device row jumps to that device on the
 * dashboard, because that list spans the whole window rather than one day.
 * The worst devices each get a daily strip, the same bars as the device
 * card, over the same window, so the days line up down the list.
 *
 * Self-gating: renders nothing for a non-admin, and the query never fires for
 * one either, so this can be mounted unconditionally.
 */

export default function MissingReadingsCard({
  className = '',
  onOpenDevice,
}: {
  className?: string
  /** Opens a device from the lists; the page owns how, so Back returns to it. */
  onOpenDevice: (sigfoxId: string) => void
}) {
  const { isAdmin } = useAuth()
  const admin = isAdmin()
  const navigate = useNavigate()
  const { devices: deviceMap } = useAccountContext()
  const [showAllDevices, setShowAllDevices] = useState(false)

  // All three live in the URL so a refresh — or a pasted link — comes back to
  // the same view. Prefixed `mr` because the dashboard URL is shared with the
  // selected-device param and whatever else lands on this page later.
  const [requestedDays, setWindowDays] = useUrlNumber('mrDays', DEFAULT_DAYS)
  // The picker cannot offer more than the fleet ceiling, but the URL can still
  // ask for it (an old link, a hand-edited ?mrDays=365). Clamp before asking,
  // and say so, rather than labelling 180 days of data as a year.
  const windowDays = Math.min(requestedDays, MISSING_READINGS_FLEET_MAX_DAYS)
  // Which account's devices the list below is scoped to. Null is the fleet's
  // worst devices, which is what an operator wants before they know where to
  // look; picking an account is the "now show me just these" follow-up.
  const [selectedAccount, setSelectedAccount] = useUrlAccount('mrAccount')
  // Defaults off, same as the per-device panel: a clean account has no missing
  // days, and opening onto an empty table reads as broken rather than as good
  // news. The window totals above stay unfiltered either way.
  const [onlyMissingDays, setOnlyMissingDays] = useUrlBoolean('mrOnlyMissing', false)

  // Two reads, one cache entry while nothing is selected: the account column
  // must always list every account, so it cannot use the scoped response.
  const { data: allAccountsSummary } = useMissingReadingsSummary(windowDays, admin)
  const { data: summary, isLoading, isError } = useMissingReadingsSummary(windowDays, admin, selectedAccount)

  if (!admin) return null

  // Prefer the ceiling the API reports over the local constant, so the notice
  // stays truthful if the server's limit is ever changed without this file.
  const fleetMax = allAccountsSummary?.maxRangeDays ?? MISSING_READINGS_FLEET_MAX_DAYS
  const clamped = requestedDays > Math.min(windowDays, fleetMax)

  // Newest first: an operator looks at yesterday far more often than at the
  // far end of a long window.
  const allDays = [...(summary?.days ?? [])].reverse()
  const days = onlyMissingDays ? allDays.filter((d) => d.missedMessages > 0) : allDays
  const totals = summary?.totals

  // The scoped response already contains only the selected account's devices.
  // The worst ones get a strip; the rest are a plain list behind a toggle.
  const allDevices = summary?.devices ?? []
  const stripDevices = allDevices.slice(0, STRIP_DEVICES)
  const moreDevices = allDevices.slice(STRIP_DEVICES)
  // Older APIs do not send the best list; the worst list then stands alone.
  const bestDevices = summary?.bestDevices ?? []
  const stripDays = Math.min(windowDays, fleetMax)
  const stripNow = new Date()
  const stripWindow = deliveryBarsForWindow([], stripDays, stripNow)
  const stripAxis = [stripWindow[0]?.day ?? '', stripWindow[stripWindow.length - 1]?.day ?? '']
  const deviceLabel = (sigfoxId: string) => (deviceMap as Record<string, DeviceInfo>)[sigfoxId]
  const accounts = allAccountsSummary?.accounts ?? []
  const selectedAccountName =
    selectedAccount === undefined
      ? null
      : accounts.find((a) => a.accountId === selectedAccount)?.accountName ?? 'No account'

  /** Day links keep the account scope, so the day page opens showing the same
   *  account the card was filtered to rather than the whole fleet. */
  const dayHref = (day: string) =>
    selectedAccount === undefined
      ? `/missing-readings/${day}`
      : `/missing-readings/${day}?accountId=${selectedAccount === null ? 'none' : selectedAccount}`

  /** One ranked list of devices, each row a strip of daily bars. The window's
   *  dates sit once over the strips rather than under every row: every strip
   *  shares the same days. */
  /** One device: name, its daily strip, and its missed count. */
  const stripRow = (dev: FleetDeviceSummary, markTolerance: boolean) => {
    const info = deviceLabel(dev.sigfoxId)
    return (
      <li key={dev.deviceId}>
        <button
          type="button"
          onClick={() => onOpenDevice(dev.sigfoxId)}
          className="flex w-full flex-col gap-1.5 rounded px-1 py-2 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:flex-row sm:items-center sm:gap-4"
        >
          <span className="min-w-0 sm:w-40 sm:flex-shrink-0">
            <span className="block truncate text-sm font-medium text-gray-900">
              {info?.deviceName || dev.sigfoxId}
            </span>
            <span className="block truncate text-xs text-gray-400">
              {dev.sigfoxId}
              {selectedAccount === undefined && dev.accountName && ` · ${dev.accountName}`}
            </span>
          </span>
          <span className="min-w-0 flex-1">
            {dev.daily ? (
              <DeliveryBars
                bars={deliveryBarsForWindow(dev.daily, stripDays, stripNow)}
                heightClass="h-7"
                stretch
                showDates={false}
              />
            ) : (
              <span className="text-xs text-gray-400">Daily detail on the device page</span>
            )}
          </span>
          <span
            className={`text-sm tabular-nums sm:w-24 sm:flex-shrink-0 sm:text-right ${
              markTolerance && dev.toleranceExceeded ? 'font-semibold text-red-600' : 'text-gray-600'
            }`}
            title={markTolerance && dev.toleranceExceeded ? 'Over the configured tolerance' : undefined}
          >
            {dev.missedMessages.toLocaleString()} missed
          </span>
        </button>
      </li>
    )
  }

  // The worst list's tail behind a toggle under its strips, drawn the same
  // way: the API sends a series for every listed device, so the pattern is
  // readable all the way down rather than only for the first ten.
  const moreDevicesList =
    moreDevices.length > 0 && (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setShowAllDevices((v) => !v)}
          aria-expanded={showAllDevices}
          className="text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          {showAllDevices ? 'Hide' : 'Show'} {moreDevices.length} more device{moreDevices.length === 1 ? '' : 's'}
        </button>
        {showAllDevices && (
          <ul aria-label="More devices" className="mt-2 divide-y divide-gray-100">
            {moreDevices.map((dev) => stripRow(dev, true))}
          </ul>
        )}
      </div>
    )

  // `markTolerance` is off for the best list: there a device can be over the
  // readings tolerance with nothing missed (it went quiet, it lost nothing),
  // and a red "0 missed" under "Best" reads as a contradiction.
  const stripColumn = (
    headingId: string,
    rank: string,
    list: FleetDeviceSummary[],
    { markTolerance = true, footer }: { markTolerance?: boolean; footer?: ReactNode } = {}
  ) =>
    list.length === 0 ? null : (
      <div className="min-w-0">
        <h4 id={headingId} className="mb-1 text-xs font-medium text-gray-500">
          {rank} {list.length} device{list.length === 1 ? '' : 's'}
        </h4>
        {list.some((dev) => dev.daily) && (
          <div aria-hidden="true" className="hidden px-1 text-[11px] tabular-nums text-gray-500 sm:flex sm:gap-4">
            <span className="sm:w-40 sm:flex-shrink-0" />
            <span className="flex flex-1 justify-between">
              <span>{stripAxis[0]}</span>
              <span>{stripAxis[1]}</span>
            </span>
            <span className="sm:w-24 sm:flex-shrink-0" />
          </div>
        )}
        <ul aria-labelledby={headingId} className="divide-y divide-gray-100">
          {list.map((dev) => stripRow(dev, markTolerance))}
        </ul>
        {footer}
      </div>
    )


  return (
    <PanelCard
      className={className}
      icon={faSatelliteDish}
      title="Missing Readings"
      actions={
        <>
          <RangeToggle value={windowDays} onChange={setWindowDays} maxDays={MISSING_READINGS_FLEET_MAX_DAYS} />
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <FontAwesomeIcon icon={faCalendarDay} className="text-gray-400" />
            <input
              type="date"
              aria-label="View a specific day"
              value=""
              min={earliestIso()}
              max={todayIso()}
              onChange={(e) => e.target.value && navigate(dayHref(e.target.value))}
              className="border border-gray-200 rounded px-1.5 py-1 text-xs text-gray-600"
            />
          </label>
        </>
      }
    >
      {clamped && (
        <p className="text-xs text-amber-700 mb-2">
          Showing {Math.min(windowDays, fleetMax)} days — the most a fleet-wide view covers. Open a device for up to a
          year.
        </p>
      )}

      {/* The fleet total spans the columns below — it describes the window,
          not any one list. */}
      {!isLoading && !isError && totals && totals.missedMessages > 0 && (
        <p className="text-xs text-gray-500 mb-2">
          {totals.missedMessages.toLocaleString()} missed message
          {totals.missedMessages === 1 ? '' : 's'} across {totals.devicesAffected} device
          {totals.devicesAffected === 1 ? '' : 's'}
        </p>
      )}

      {/* Account · Day, then the devices underneath. The account column is
          the filter: picking one rescopes the day series and the device list,
          so the card reads as one question narrowing. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3 items-start">
        {/* Column 1 — always the full account list, never scoped. */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">By Account</p>
          <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto pr-2 [scrollbar-gutter:stable]">
            <li
              onClick={() => setSelectedAccount(undefined)}
              aria-current={selectedAccount === undefined ? 'true' : undefined}
              className={`py-1 px-1 -mx-1 rounded flex items-center justify-between gap-2 text-xs cursor-pointer ${
                selectedAccount === undefined ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
              }`}
            >
              <span className={selectedAccount === undefined ? 'text-blue-800 font-medium' : 'text-gray-700'}>
                All accounts
                <span className={selectedAccount === undefined ? 'text-blue-400 ml-1' : 'text-gray-400 ml-1'}>
                  ({allAccountsSummary?.totals.devicesAffected ?? 0})
                </span>
              </span>
              <span className="tabular-nums flex-shrink-0 text-gray-600">
                {(allAccountsSummary?.totals.missedMessages ?? 0).toLocaleString()}
              </span>
            </li>
            {accounts.map((acct) => {
              const picked = selectedAccount !== undefined && selectedAccount === acct.accountId
              return (
                <li
                  key={acct.accountId ?? 'none'}
                  onClick={() => setSelectedAccount(picked ? undefined : acct.accountId)}
                  aria-current={picked ? 'true' : undefined}
                  className={`py-1 px-1 -mx-1 rounded flex items-center justify-between gap-2 text-xs cursor-pointer ${
                    picked ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`truncate ${picked ? 'text-blue-800 font-medium' : 'text-gray-700'}`}
                    title={acct.accountName ?? undefined}
                  >
                    {acct.accountName ?? 'No account'}
                    <span className={picked ? 'text-blue-400 ml-1' : 'text-gray-400 ml-1'}>
                      ({acct.devicesAffected}/{acct.deviceCount})
                    </span>
                  </span>
                  <span
                    className={`tabular-nums flex-shrink-0 ${
                      acct.missedMessages === 0
                        ? 'text-gray-400'
                        : acct.toleranceExceeded
                        ? 'text-red-600 font-semibold'
                        : 'text-gray-600'
                    }`}
                    title={
                      acct.toleranceExceeded
                        ? 'Over the configured tolerance'
                        : `${acct.devicesAffected} of ${acct.deviceCount} devices affected`
                    }
                  >
                    {acct.missedMessages.toLocaleString()}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>

        {/* Column 2 — the day series, scoped to the selected account. */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-xs font-medium text-gray-500 truncate">
              By Day{selectedAccountName ? ` — ${selectedAccountName}` : ''}
            </p>
            <LabeledSwitch
              checked={onlyMissingDays}
              onCheckedChange={setOnlyMissingDays}
              label="Only missing"
              title="Hide days with no missed messages"
            />
          </div>
          {/* While loading, the spinner below the columns says so. */}
          {isLoading ? null : isError ? (
            <p className="text-sm text-gray-400">Could not load the missing-readings summary</p>
          ) : !totals || totals.missedMessages === 0 ? (
            <p className="text-sm text-gray-400">No missed messages in the last {windowDays} days</p>
          ) : (
            <>
              <div className="max-h-72 overflow-y-auto pr-3 [scrollbar-gutter:stable]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-gray-400 border-b border-gray-100">
                      <th className="py-1 pr-2 text-left font-medium">Day</th>
                      <th className="py-1 pr-2 text-right font-medium">Missed</th>
                      <th className="py-1 text-right font-medium">Devices</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {days.map((d) => (
                      <tr key={d.day} className="hover:bg-gray-50">
                        <td className="py-1 pr-2 text-gray-700">
                          <Link to={dayHref(d.day)} className="text-blue-600 hover:underline">
                            {d.day}
                          </Link>
                          {d.devicesReconfigured > 0 && (
                            <ReconfiguredMark
                              title={`${d.devicesReconfigured} device(s) reconfigured this day — no expected-count estimate`}
                            />
                          )}
                        </td>
                        <td
                          className={`py-1 pr-2 text-right tabular-nums ${
                            d.missedMessages > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'
                          }`}
                        >
                          {d.missedMessages}
                        </td>
                        <td className="py-1 text-right tabular-nums text-gray-500">{d.devicesAffected || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

      </div>

      {/* A long window takes seconds to compute, and until it arrives the card
          is two near-empty columns. The spinner sits where the device strips
          will appear, so the space reads as "coming" rather than "empty". */}
      {isLoading && (
        <div className="mt-5 flex flex-col items-center justify-center gap-3 border-t border-gray-100 py-24 text-sm text-gray-500">
          <LoadingSpinner size="lg" />
          Loading the last {windowDays} days…
        </div>
      )}

      {/* The devices behind those days: the best beside the worst, each with
          its own strip, so a bad pattern can be read against a good one. */}
      {summary && (stripDevices.length > 0 || bestDevices.length > 0) && (
        <section aria-label="Devices" className="mt-5 border-t border-gray-100 pt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              {selectedAccountName ?? 'All accounts'} · last {stripDays} day{stripDays === 1 ? '' : 's'}
            </p>
            <DeliveryLegend />
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
            {stripColumn('mr-best-heading', 'Best', bestDevices, { markTolerance: false })}
            {stripColumn('mr-worst-heading', 'Worst', stripDevices, { footer: moreDevicesList })}
          </div>
        </section>
      )}
    </PanelCard>
  )
}
